import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Mail, Sparkles, Linkedin } from "lucide-react";
import { Logo } from "@/components/Header";
import { Leaf, HandUnderline } from "@/components/home/Doodles";

const kolommen = [
  {
    kop: "Snel naar",
    items: [
      { l: "Voor gemeenten", to: "/gemeenten" },
      { l: "Voor deelnemers", to: "/voor-jou" },
      { l: "Onze aanpak", to: "/methode" },
      { l: "Deelnemersverhalen", to: "/verhalen" },
      { l: "Voor onderwijs", to: "/onderwijs" },
      { l: "Voor partners", to: "/partners" },
    ],
  },
  {
    kop: "START-programma",
    items: [
      { l: "START — 12 weken", to: "/programma?programma=start" },
      { l: "VERDER — mogelijk vervolg", to: "/programma?programma=verder" },
      { l: "De gemeentelijke pilot", to: "/pilot" },
      { l: "Onderzoek & onderbouwing", to: "/onderzoek-en-onderbouwing" },
      { l: "Brochure (PDF)", to: "/downloads/Levelleven-brochure-v16.pdf", external: true },
    ],
  },
  {
    kop: "Ontdekken",
    items: [
      { l: "Over ons", to: "/over-ons" },
      { l: "Manifest", to: "/manifest" },
      { l: "Kennisbank", to: "/kennisbank" },
      { l: "Vraag & Antwoord", to: "/faq" },
      { l: "Probeer de Vonkvinder", to: "/vonkvinder" },
    ],
  },
];

const NO_PREFOOTER = ["/", "/vonkvinder", "/gemeenten", "/voor-gemeenten", "/voor-opdrachtgevers", "/pilot", "/programmabouwer", "/onderzoek-en-onderbouwing"];

function PreFooter() {
  return (
    <section className="py-20 sm:py-28 relative overflow-hidden" data-testid="pre-footer">
      <div className="max-w-[1200px] mx-auto px-6 sm:px-10 text-center relative">
        <p className="font-hand text-2xl text-ll-orange">Een laatste vraag</p>
        <h2 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ll-forest">
          Waar zou jij weer <span className="italic text-ll-orange">zin in willen krijgen</span>?
        </h2>
        <Link to="/vonkvinder" data-testid="prefooter-vonk-cta" className="group mt-8 inline-flex items-center gap-3 text-lg font-heading italic text-ll-forest hover:text-ll-orange transition-colors">
          <Sparkles size={18} className="text-ll-orange" />
          <span className="link-underline">Vind eerst je vonk</span>
          <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
        </Link>
      </div>
    </section>
  );
}

function FooterLink({ i }) {
  const cls = "text-ll-ink/75 hover:text-ll-orange transition-colors";
  return i.external ? (
    <a href={i.to} target="_blank" rel="noreferrer" className={cls}>{i.l}</a>
  ) : (
    <Link to={i.to} className={cls}>{i.l}</Link>
  );
}

export default function Footer() {
  const { pathname } = useLocation();
  return (
    <>
      {!NO_PREFOOTER.includes(pathname) && <PreFooter />}
      <footer className="relative bg-white/60 border-t border-ll-forest/10 overflow-hidden" data-testid="site-footer">
        <Leaf className="absolute right-6 top-10 w-24 text-ll-sage opacity-70 hidden md:block" />
        <div className="relative max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12 pt-16 sm:pt-20 pb-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-14">
            <div className="md:col-span-4">
              <Link to="/" aria-label="LevelLeven home" data-testid="footer-logo"><Logo className="h-11" /></Link>
              <p className="mt-6 text-sm text-ll-ink/75 leading-relaxed max-w-sm">
                LevelLeven helpt mensen die al langer stilstaan weer in beweging te komen. Met twaalf weken persoonlijke ontwikkeling, inzicht en een passende volgende stap.
              </p>
              <div className="mt-6 flex flex-col gap-2 text-sm">
                <Link to="/contact" data-testid="footer-cta-primary" className="inline-flex items-center gap-2 text-ll-forest font-semibold hover:text-ll-orange transition-colors">
                  Neem contact op <ArrowUpRight size={15} />
                </Link>
                <a href="mailto:info@levelleven.nl" data-testid="footer-cta-mail" className="inline-flex items-center gap-2 text-ll-ink/75 hover:text-ll-orange transition-colors">
                  <Mail size={14} /> info@levelleven.nl
                </a>
                <a href="https://www.linkedin.com/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-ll-ink/75 hover:text-ll-orange transition-colors">
                  <Linkedin size={14} /> LinkedIn
                </a>
              </div>
            </div>
            <div className="md:col-span-8 grid grid-cols-2 sm:grid-cols-3 gap-8">
              {kolommen.map((k) => (
                <div key={k.kop}>
                  <p className="text-sm font-semibold text-ll-forest mb-4">{k.kop}</p>
                  <ul className="space-y-2.5 text-sm">
                    {k.items.map((i) => <li key={i.l}><FooterLink i={i} /></li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-14 pt-8 border-t border-ll-forest/10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <p className="font-hand text-3xl leading-tight text-ll-forest">
              Iedereen heeft talent. <span className="relative inline-block">Soms zit er alleen iets tussen.<HandUnderline className="absolute -bottom-2 left-0 w-full text-ll-orange" /></span>
            </p>
            <p className="font-hand text-3xl leading-tight text-ll-forest md:text-right">
              Het leven mag ondertussen ook gewoon weer leuk worden.
            </p>
          </div>

          <div className="mt-10 pt-6 border-t border-ll-forest/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-ll-ink/60">
            <p>© {new Date().getFullYear()} LevelLeven · Een initiatief van OutsideTen</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/privacy" className="hover:text-ll-orange transition-colors">Privacy</Link>
              <Link to="/voorwaarden" className="hover:text-ll-orange transition-colors">Voorwaarden</Link>
              <Link to="/cookies" className="hover:text-ll-orange transition-colors">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
